import { useEffect, useState, type ReactNode } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '~/stores/auth-store';
import { AppSplash } from '~/components/ui/app-splash';

// Rendered in the initial HTML, outside AuthGuard, and dismissed once per page load.
export function AppStartup({ children }: { children: ReactNode }) {
	const [phase, setPhase] = useState<'loading' | 'leaving' | 'hidden'>('loading');
	const [documentReady, setDocumentReady] = useState(false);
	const hydrated = useAuthStore((state) => state._hasHydrated);
	const routeReady = useRouterState({ select: (state) => state.status === 'idle' && !state.isLoading });
	const pending = useIsFetching({ predicate: (query) => query.state.status === 'pending' });
	const ready = hydrated && documentReady && routeReady && pending === 0;

	useEffect(() => {
		let active = true;
		const loaded = () => {
			void (document.fonts?.ready ?? Promise.resolve()).then(() => {
				if (active) setDocumentReady(true);
			});
		};
		if (document.readyState === 'complete') loaded();
		else window.addEventListener('load', loaded, { once: true });
		return () => {
			active = false;
			window.removeEventListener('load', loaded);
		};
	}, []);

	useEffect(() => {
		if (phase === 'hidden') return;
		if (!ready) {
			if (phase === 'leaving') setPhase('loading');
			return;
		}
		if (phase === 'loading') {
			// Let the initial route mount its queries before testing readiness again.
			let frame = requestAnimationFrame(() => {
				frame = requestAnimationFrame(() => setPhase('leaving'));
			});
			return () => cancelAnimationFrame(frame);
		}
		const timer = window.setTimeout(
			() => setPhase('hidden'),
			window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220,
		);
		return () => window.clearTimeout(timer);
	}, [ready, phase]);

	return (
		<>
			<div
				style={{ display: 'contents' }}
				inert={phase !== 'hidden'}
				aria-hidden={phase !== 'hidden' ? true : undefined}
			>
				{children}
			</div>
			{phase !== 'hidden' && <AppSplash leaving={phase === 'leaving'} />}
		</>
	);
}
